import React, { useState } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { SYMPTOMS_BY_TYPE, HDType } from '@/lib/types';
import { ENERGY_CENTERS, analyzeEnergyCenters } from '@/lib/energyCenters';
import { Plus, Save, Trash2, Brain, MessageCircle, Lightbulb, Check } from 'lucide-react';

interface Symptom {
  text: string;
  response: string;
  recommendations: string[];
  energyImpact: number;
  manualCenters?: string[]; // Centres sélectionnés manuellement
}

export function AdminSymptoms() {
  const [selectedType, setSelectedType] = useState<HDType>('projector');
  const [symptoms, setSymptoms] = useState<Record<HDType, Symptom[]>>(
    Object.entries(SYMPTOMS_BY_TYPE).reduce((acc, [type, symptoms]) => ({
      ...acc,
      [type]: symptoms.map(s => ({
        ...s,
        recommendations: [],
        manualCenters: []
      }))
    }), {} as Record<HDType, Symptom[]>)
  );
  const [editing, setEditing] = useState(false);
  const { toast } = useToast();

  const handleSave = () => {
    // TODO: Implémenter la sauvegarde dans la base de données
    toast({
      title: "Modifications enregistrées",
      description: "Les symptômes ont été mis à jour avec succès.",
    });
    setEditing(false);
  };

  const addSymptom = () => {
    setSymptoms(prev => ({
      ...prev,
      [selectedType]: [
        ...prev[selectedType],
        {
          text: "",
          response: "",
          recommendations: [],
          energyImpact: 0,
          manualCenters: []
        }
      ]
    }));
    setEditing(true);
  };

  const updateSymptom = (index: number, field: string, value: string | number | string[]) => {
    setSymptoms(prev => ({
      ...prev,
      [selectedType]: prev[selectedType].map((symptom, i) => 
        i === index ? { ...symptom, [field]: value } : symptom
      )
    }));
    setEditing(true);
  };

  const addRecommendation = (index: number) => {
    setSymptoms(prev => ({
      ...prev,
      [selectedType]: prev[selectedType].map((symptom, i) => 
        i === index 
          ? { ...symptom, recommendations: [...symptom.recommendations, ""] }
          : symptom
      )
    }));
    setEditing(true);
  };

  const updateRecommendation = (symptomIndex: number, recIndex: number, value: string) => {
    setSymptoms(prev => ({
      ...prev,
      [selectedType]: prev[selectedType].map((symptom, i) => 
        i === symptomIndex 
          ? {
              ...symptom,
              recommendations: symptom.recommendations.map((rec, j) => 
                j === recIndex ? value : rec
              )
            }
          : symptom
      )
    }));
    setEditing(true);
  };

  const removeRecommendation = (symptomIndex: number, recIndex: number) => {
    setSymptoms(prev => ({
      ...prev,
      [selectedType]: prev[selectedType].map((symptom, i) => 
        i === symptomIndex 
          ? {
              ...symptom,
              recommendations: symptom.recommendations.filter((_, j) => j !== recIndex)
            }
          : symptom
      )
    }));
    setEditing(true);
  };

  const removeSymptom = (index: number) => {
    setSymptoms(prev => ({
      ...prev,
      [selectedType]: prev[selectedType].filter((_, i) => i !== index)
    }));
    setEditing(true);
  };

  const toggleCenter = (symptomIndex: number, centerName: string) => {
    setSymptoms(prev => ({
      ...prev,
      [selectedType]: prev[selectedType].map((symptom, i) => {
        if (i !== symptomIndex) return symptom;
        
        const currentCenters = symptom.manualCenters || [];
        const newCenters = currentCenters.includes(centerName)
          ? currentCenters.filter(c => c !== centerName)
          : [...currentCenters, centerName];
        
        return {
          ...symptom,
          manualCenters: newCenters
        };
      })
    }));
    setEditing(true);
  };

  const getAffectedCenters = (symptom: string) => {
    const { affectedCenters } = analyzeEnergyCenters(selectedType, [symptom]);
    return affectedCenters;
  };

  const allCenters = Object.entries(ENERGY_CENTERS);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <div className="space-y-1">
          <h3 className="text-lg font-semibold">Gestion des symptômes</h3>
          <p className="text-sm text-gray-500">
            Modifiez les symptômes, réponses et conseils par type
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value as HDType)}
            className="border rounded-md px-3 py-2"
          >
            <option value="projector">Projector</option>
            <option value="generator">Generator</option>
            <option value="manifesting-generator">Manifesting Generator</option>
            <option value="manifestor">Manifestor</option>
            <option value="reflector">Reflector</option>
          </select>
          <Button
            onClick={addSymptom}
            variant="outline"
            className="flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Ajouter
          </Button>
          {editing && (
            <Button
              onClick={handleSave}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
            >
              <Save className="w-4 h-4" />
              Enregistrer
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {symptoms[selectedType].map((symptom, index) => (
            <Card key={index} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="space-y-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Symptôme
                        </label>
                        <input
                          type="text"
                          value={symptom.text}
                          onChange={(e) => updateSymptom(index, 'text', e.target.value)}
                          className="w-full px-3 py-2 border rounded-md"
                        />
                      </div>

                      <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                          <MessageCircle className="w-4 h-4" />
                          Réponse personnalisée
                        </label>
                        <Textarea
                          value={symptom.response}
                          onChange={(e) => updateSymptom(index, 'response', e.target.value)}
                          className="w-full"
                          placeholder="Message de réponse pour ce symptôme..."
                        />
                      </div>

                      <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                          <Lightbulb className="w-4 h-4" />
                          Conseils et recommandations
                        </label>
                        <div className="space-y-2">
                          {symptom.recommendations.map((rec, recIndex) => (
                            <div key={recIndex} className="flex items-start gap-2">
                              <input
                                type="text"
                                value={rec}
                                onChange={(e) => updateRecommendation(index, recIndex, e.target.value)}
                                className="flex-1 px-3 py-2 border rounded-md"
                                placeholder="Conseil ou recommandation..."
                              />
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => removeRecommendation(index, recIndex)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => addRecommendation(index)}
                            className="w-full"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Ajouter un conseil
                          </Button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Impact énergétique (-10 à +10)
                        </label>
                        <input
                          type="number"
                          min="-10"
                          max="10"
                          value={symptom.energyImpact}
                          onChange={(e) => updateSymptom(index, 'energyImpact', parseInt(e.target.value))}
                          className="w-full px-3 py-2 border rounded-md"
                        />
                      </div>

                      <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                          <Brain className="w-4 h-4" />
                          Centres énergétiques affectés
                        </label>
                        
                        {/* Suggestion automatique */}
                        <div className="mb-2">
                          <p className="text-sm text-gray-600 mb-2">Suggestion automatique :</p>
                          <div className="flex flex-wrap gap-2">
                            {symptom.text && getAffectedCenters(symptom.text).map((center, i) => (
                              <span
                                key={i}
                                className="px-2 py-1 rounded-md text-sm bg-purple-100 text-purple-800"
                              >
                                {center}
                              </span>
                            ))}
                            {symptom.text && getAffectedCenters(symptom.text).length === 0 && (
                              <span className="text-sm text-gray-500 italic">
                                Aucun centre suggéré
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Sélection manuelle */}
                        <div>
                          <p className="text-sm text-gray-600 mb-2">Sélection manuelle :</p>
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                            {allCenters.map(([key, center]) => {
                              const isSelected = symptom.manualCenters?.includes(center.name);
                              return (
                                <Button
                                  key={key}
                                  variant="outline"
                                  size="sm"
                                  onClick={() => toggleCenter(index, center.name)}
                                  className={`flex items-center justify-between gap-2 ${
                                    isSelected 
                                      ? 'bg-purple-100 border-purple-300 text-purple-800' 
                                      : ''
                                  }`}
                                >
                                  <span className="truncate">{center.name}</span>
                                  {isSelected && <Check className="w-4 h-4 shrink-0" />}
                                </Button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => removeSymptom(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}